import React, { useEffect, useRef, useState } from 'react';
import { ArrowRight, Flame, Scissors } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { api } from './api';
import { usePlatform } from './PlatformContext';
import { utcDate } from './dailyData';
import { isReminderRoute, markReminderShown, reminderDismissed, reminderVisit } from './reminderState';
import './streaks.css';

export const DailyReminder = ({ blocked = false }) => {
  const { view, user, guest, scope, day, authLoading, authOpen, navigate, version } = usePlatform();
  const [visit] = useState(reminderVisit);
  const [candidate, setCandidate] = useState(null), [activity, setActivity] = useState(0);
  const primary = useRef(null);
  const key = `${scope}:${day}:${user?.id || guest?.id || 'guest'}`;
  const knownAccount = user?.created_at && Date.parse(user.created_at) < visit.startedAt;
  const eligible = visit.storageAvailable && (visit.returning || !!knownAccount) && !blocked && !authOpen && !authLoading && !!scope && isReminderRoute(view);
  useEffect(() => {
    const recheck = () => setActivity(value => value + 1);
    window.addEventListener('storage', recheck);
    window.addEventListener('online', recheck);
    window.addEventListener('focus', recheck);
    document.addEventListener('visibilitychange', recheck);
    return () => {
      window.removeEventListener('storage', recheck); window.removeEventListener('online', recheck);
      window.removeEventListener('focus', recheck); document.removeEventListener('visibilitychange', recheck);
    };
  }, []);
  useEffect(() => {
    let alive = true;
    setCandidate(null);
    if (!eligible || reminderDismissed(scope, day)) return;
    const unobstructed = () => alive && day === utcDate() && document.visibilityState === 'visible' && !document.querySelector('[role="dialog"][data-state="open"]');
    const timer = setTimeout(async () => {
      if (!unobstructed()) return;
      try {
        const summary = await api('/daily/summary');
        if (!unobstructed() || summary.date !== day || summary.scope !== scope || summary.user_id !== (user?.id || null) || summary.completed_today) return;
        if (!user?.alias_confirmed && (summary.guest?.id || null) !== (guest?.id || null)) return;
        const show = () => {
          if (!unobstructed() || reminderDismissed(scope, day)) return;
          markReminderShown(scope, day); // Claim once-per-day before opening, including across tabs.
          setCandidate({ ...summary, key });
        };
        if (navigator.locks?.request) await navigator.locks.request(`daily-reminder:${scope}:${day}`, { ifAvailable: true }, lock => { if (lock) show(); });
        else show();
      } catch { /* Optional reminder: never interrupt on unverified state or network failure. */ }
    }, 1500);
    return () => { alive = false; clearTimeout(timer); };
  }, [eligible, scope, day, user?.id, user?.alias_confirmed, guest?.id, key, view, version, activity]);
  const open = !!candidate && candidate.key === key && eligible && day === utcDate();
  const activeStreak = candidate?.streak?.active_at_risk && candidate.streak.as_of_date_utc === day ? candidate.streak.current : 0;
  const close = () => { markReminderShown(scope, day); setCandidate(null); };
  return <Dialog open={open} onOpenChange={next => { if (!next) close(); }}>
    <DialogContent className="ps-identity daily-reminder-dialog" data-testid="daily-reminder-modal" closeTestId="daily-reminder-close-button" onOpenAutoFocus={event => { event.preventDefault(); primary.current?.focus(); }}>
      <div className="daily-reminder-icon" data-testid="daily-reminder-modal-icon" aria-hidden="true">{activeStreak ? <Flame/> : <Scissors/>}</div>
      <span className="daily-reminder-eyebrow" data-testid="daily-reminder-eyebrow">A LITTLE DAILY CHALLENGE</span>
      <DialogTitle data-testid="daily-reminder-modal-title">{activeStreak ? `Keep your ${activeStreak}-day streak going` : 'Today’s Daily Slice is ready'}</DialogTitle>
      <DialogDescription data-testid="daily-reminder-modal-body">{activeStreak ? <>Today’s Daily Slice is ready.<br/>Three cuts. Your best counts.</> : 'Same shape for everyone. Three chances.'}</DialogDescription>
      <Button ref={primary} className="primary-button" data-testid="daily-reminder-play-today-button" onClick={() => { close(); navigate('/perfectslice/daily'); }}>PLAY TODAY’S SLICE<ArrowRight size={18}/></Button>
      <Button variant="ghost" className="daily-reminder-later" data-testid="daily-reminder-maybe-later-button" onClick={close}>MAYBE LATER</Button>
    </DialogContent>
  </Dialog>;
};
